import { useState } from "react";
import { BackButton } from "../../components/BackButton";

/**
 * The Prep AI Privacy Policy, as published by Publica AI (v2, effective
 * 21/09/2026). Copied from PREP_AI_v2_PRIVACY_POLICY_v2 word for word; only
 * its layout is turned into sections, paragraphs and "•" bullet lines.
 */
const EFFECTIVE = "21 September 2026";
const LAST_UPDATED = "21 September 2026";

const sections: { title: string; content: string[] }[] = [
  {
    title: "1. Introduction",
    content: [
      'Prep AI is an AI-powered educational platform operated by Publica AI Limited ("Publica AI", "we", "us", or "our"). Prep AI is designed to help students prepare for examinations and improve their learning through AI-supported practice, assessments, and personalised learning experiences.',
      "This Privacy Policy explains how we collect, use, store, protect, disclose, and otherwise process personal data when you use Prep AI.",
      "We are committed to processing personal data in accordance with the Nigeria Data Protection Act, 2023 (NDPA), the Nigeria Data Protection Act General Application and Implementation Directive 2025 (GAID), and other applicable data protection requirements.",
      "By using Prep AI, you acknowledge that you have read and understood this Privacy Policy and how your personal data may be processed.",
      "Where parental or guardian consent is required for a user under 18, acceptance of this Privacy Policy by the student does not replace that parental or guardian consent. The required parental or guardian approval must be obtained separately.",
    ],
  },
  {
    title: "2. Who Is Responsible for Your Data?",
    content: [
      "Publica AI Limited is the data controller responsible for personal data processed through Prep AI.",
      "Registered Office:\nNo. 8 Montgomery Street, Yaba, Lagos, Nigeria",
      "Email: privacy@prepai.education\nPhone: +234 911 243 0001",
      "As the data controller, Publica AI determines the purposes for which personal data is processed and the manner in which it is processed.",
    ],
  },
  {
    title: "3. What Personal Data Do We Collect?",
    content: [
      "We collect information that is necessary to provide and operate Prep AI. The information we collect depends on how you use the Platform.",
      "3.1 Account and Registration Information",
      "When you create an account, we may collect:",
      "• Your phone number;\n• Your email address, where you choose to provide one;\n• Your password or authentication information; and\n• Verification information, such as verification codes.",
      "Your phone number is used as the primary account identifier. Providing an email address is optional and may provide an additional way to receive verification codes and payment or service-related receipts.",
      "We do not require a full name to create a Prep AI account.",
      "3.2 Academic and Practice Information",
      "When you use Prep AI for learning and examination preparation, we may process information such as:",
      "• Your selected examination type, such as WAEC, JAMB or NECO;\n• Subjects selected for practice;\n• Quiz and test results;\n• Practice and study progress;\n• Readiness scores and related learning information; and\n• Other information generated through your use of Prep AI's learning features.",
      "This information helps us provide the learning and practice services offered through Prep AI.",
      "3.3 AI Interaction Information",
      "When you interact with Prep AI's AI-powered features, information relating to your interaction may be processed in order for the AI system to respond to your requests and provide the relevant educational service.",
      "Depending on the feature you use, this may include your questions, prompts, responses, and information necessary to generate or evaluate your learning activities.",
      "Information submitted through Prep AI may be processed by the AI systems used to provide the service.",
      "We do not require you to provide personal information that is unnecessary for receiving educational assistance. You should avoid including sensitive personal information in questions or messages unless it is necessary for the service.",
      "3.4 Technical and Usage Information",
      "We may collect technical information generated when you use Prep AI, including:",
      "• Login and access logs;\n• Information necessary to maintain account and platform security; and\n• Information relating to your use of the Platform.",
      "We use this information primarily to operate, secure, monitor, and improve the Platform.",
      "3.5 Parent or Guardian Information",
      "Where a user identifies as being under 18 and parental or guardian approval is required, we may collect the parent's or legal guardian's:",
      "• Email address; and/or\n• Phone number.",
      "This information is used to contact the parent or guardian and facilitate the required consent process.",
      "3.6 Payment and Transaction Information",
      "Where Prep AI offers paid services or features, payments are processed through Paystack.",
      "Publica AI does not directly collect or store users' full card or bank account details for payment processing through Prep AI. Payment information is handled by Paystack in accordance with its own privacy and security practices.",
      "We may retain limited transaction information necessary to confirm and manage a payment, such as transaction references, payment status, amount, and information associated with the Prep AI account.",
    ],
  },
  {
    title: "4. Why Do We Use Your Personal Data?",
    content: [
      "We process personal data for purposes including:",
      "• Creating and managing Prep AI accounts;\n• Authenticating users and sending verification codes;\n• Providing AI-powered educational and examination preparation services;\n• Delivering practice questions, assessments, and learning features;\n• Recording and displaying academic progress and practice results;\n• Personalising learning experiences;\n• Processing and confirming payments;\n• Sending payment and service-related receipts and communications;\n• Facilitating parental or guardian approval where required;\n• Maintaining the security and integrity of the Platform;\n• Detecting, preventing, and investigating misuse or unauthorised access;\n• Monitoring and improving Platform performance;\n• Providing technical support and resolving service issues;\n• Complying with applicable legal and regulatory requirements; and\n• Responding to lawful requests and exercising or defending legal rights.",
      "Learning Opportunities and Programme Updates",
      "Where you have given your consent, Publica AI may use your contact details to send you updates about its training programmes, educational opportunities, workshops, learning resources, and related initiatives. These communications are optional and separate from messages necessary to operate your Prep AI account. Choosing not to receive programme updates will not affect your access to Prep AI.",
      "You may withdraw your consent at any time using the unsubscribe option in our communications or by contacting us. Once you withdraw consent, we will stop using your personal data for these communications. Where a third-party email service is used, relevant contact information may be processed by that provider to deliver the messages.",
      "We do not sell users' personal data.",
    ],
  },
  {
    title: "5. Lawful Bases for Processing",
    content: [
      "We process personal data only where there is a lawful basis for doing so under applicable Nigerian data protection law.",
      "Depending on the particular processing activity, our lawful bases may include:",
      "Performance of a contract",
      "We may process information necessary to create and operate your account and provide the educational services you request.",
      "Consent",
      "We may rely on consent where consent is required or appropriate, including certain optional processing activities and parental or guardian consent where applicable for users under 18.",
      "Where we rely on consent, you may withdraw that consent subject to any applicable legal or operational limitations.",
      "Legal obligation",
      "We may process personal data where necessary to comply with a legal or regulatory obligation.",
      "Legitimate interests",
      "We may process personal data where necessary for legitimate interests, such as maintaining platform security, preventing fraud or misuse, and improving the reliability of our services, provided that those interests do not override applicable data protection rights and freedoms.",
      "We determine the appropriate lawful basis based on the particular processing activity rather than treating all processing as being based on consent.",
    ],
  },
  {
    title: "6. Children and Parental/Guardian Consent",
    content: [
      "Prep AI is intended to support students, including users who may be under 18.",
      "We take the protection of children's personal data seriously and apply additional safeguards where required.",
      "When creating an account, users are asked:",
      '"Are you under 18?"',
      "Users are expected to provide an honest response because the answer determines whether an additional parental or guardian approval step is required.",
      "Where a user identifies as being under 18 and parental or guardian approval is required:",
      "1. The parent or legal guardian is contacted using the provided email address or phone number.",
      "2. A consent request or consent link is sent to the parent or guardian.",
      "3. The required parental or guardian approval must be obtained before the minor is permitted to continue using Prep AI where such approval is required.",
      "4. The parent or guardian may exercise applicable rights regarding the child's personal data.",
      "The minor's acceptance of this Privacy Policy does not itself constitute parental or guardian consent.",
      "We collect only the information reasonably necessary to facilitate the parental or guardian approval process.",
      "Where parental or guardian consent is withdrawn, or where the required consent is not obtained, access to the relevant services may be restricted or terminated.",
    ],
  },
  {
    title: "7. How Prep AI Uses Artificial Intelligence",
    content: [
      "Prep AI uses artificial intelligence to support educational activities, including responding to questions, generating or supporting practice activities, and providing learning assistance.",
      "AI-generated content is intended to support learning and should not be treated as a substitute for teachers, official examination authorities, or other authoritative educational sources.",
      "Information submitted through Prep AI may be processed by the AI systems used to provide the service. We take reasonable steps to ensure that personal data used in connection with AI features is processed for legitimate and specified purposes.",
      "We will not intentionally use Prep AI personal data for unrelated purposes merely because the data is available to us.",
    ],
  },
  {
    title: "8. Third-party Service Providers",
    content: [
      "We use selected third-party service providers to operate and support Prep AI.",
      "These may include providers for:",
      "• Email and communications;\n• Payment processing;\n• Analytics;\n• Cloud/database infrastructure;\n• AI-related services; and\n• Other technical services necessary to operate the Platform.",
      "For example, SendGrid, a service provided by Twilio, is used for email-related services and Paystack is used for payment processing.",
      "Third-party providers may process personal data on our behalf and are expected to process it only for authorised purposes and in accordance with applicable contractual and data protection requirements.",
      "Where a third-party provider processes personal data outside Nigeria, applicable requirements for international data transfers will be considered and appropriate safeguards will be applied.",
    ],
  },
  {
    title: "9. Payment and Email Services",
    content: [
      "9.1 Paystack",
      "Prep AI uses Paystack to process payments.",
      "When you make a payment, information necessary to complete and verify the transaction may be transferred to Paystack.",
      "Publica AI does not directly store your full card or bank account details for payment processing.",
      "Payment processing is subject to Paystack's own terms and privacy practices in addition to this Privacy Policy.",
      "Payment-related personal data processed through Paystack is retained in accordance with Paystack's applicable retention policies and relevant legal and regulatory requirements.",
      "9.2 SendGrid",
      "Prep AI uses SendGrid, a service provided by Twilio, to send emails such as payment receipts, verification-related communications, and other service-related emails.",
      "Your email address and information necessary to send the relevant communication may be processed by SendGrid.",
      "SendGrid/Twilio's retention practices apply to data processed through its services. Twilio states that Customer Account Data is retained for as long as necessary to provide its services and operate its business, subject to applicable retention policies, deletion rights, and limitations.",
      "Publica AI does not represent that SendGrid retains all data for a fixed period or indefinitely.",
    ],
  },
  {
    title: "10. Data Storage",
    content: [
      "Personal data processed through Prep AI is stored using our technology infrastructure, including the GBB database.",
      "Based on our current technical controls:",
      "• Personal data is encrypted in transit and at rest;\n• Access to production data is restricted;\n• Production and development environments are separated; and\n• Backups are maintained to support service continuity and recovery.",
      "Access to production personal data is limited to authorised personnel whose responsibilities require such access.",
      "We review and maintain appropriate technical and organisational measures to protect personal data against unauthorised access, loss, destruction, alteration, or other unlawful processing.",
      "However, no digital service can guarantee absolute security.",
    ],
  },
  {
    title: "11. Data Sharing and Disclosure",
    content: [
      "We do not sell or rent personal data.",
      "We may disclose or provide access to personal data where necessary for the purposes described in this Privacy Policy, including to:",
      "• Service providers that support Prep AI;\n• Payment processors;\n• Email and communications providers;\n• Cloud, database, analytics, or technical service providers;\n• AI service providers where required to provide AI functionality;\n• Regulators, government authorities, or law enforcement where disclosure is required or permitted by law;\n• Professional advisers where necessary to protect or exercise legal rights; and\n• Other parties where necessary in connection with a lawful corporate transaction.",
      "We take reasonable steps to ensure that third parties processing personal data on our behalf provide appropriate safeguards for that data.",
    ],
  },
  {
    title: "12. International Data Transfers",
    content: [
      "Some of our service providers or technology infrastructure may be located outside Nigeria.",
      "Where personal data is transferred or made accessible outside Nigeria, Publica AI will apply the safeguards required under applicable Nigerian data protection law, including requirements relating to lawful international transfers and appropriate protection mechanisms.",
      "Where appropriate, information about relevant third-party processing arrangements may be provided upon request.",
    ],
  },
  {
    title: "13. Data Retention",
    content: [
      "We retain personal data only for as long as reasonably necessary for the purposes for which it was collected or processed, or where longer retention is required or permitted by law.",
      "13.1 Account and Learning Data",
      "When you delete your Prep AI account:",
      "• Your account is marked for deletion;\n• You will no longer be able to access the account;\n• Your account and associated data enter a 30-day deletion period; and\n• At the end of the 30-day period, the retained personal data is permanently deleted, subject to information that we are legally required or permitted to retain for a longer period.",
      "This may include account information and associated learning data such as practice data, readiness scores, streaks, and related information.",
      "The 30-day period is intended to support operational and security requirements associated with account deletion.",
      "Where data is retained in backups or systems subject to separate deletion cycles, deletion may occur in accordance with the applicable backup or system retention process.",
      "13.2 Paystack",
      "Payment-related personal data processed through Paystack is retained in accordance with Paystack's applicable retention policies and relevant legal and regulatory requirements.",
      "Publica AI does not independently determine the retention period for information that Paystack is required to retain under its own legal, regulatory, or operational obligations.",
      "13.3 SendGrid",
      "Personal data processed through SendGrid is retained in accordance with Twilio/SendGrid's applicable retention policies and guidelines.",
      "Twilio states that Customer Account Data is stored for as long as needed to provide its services and operate its business, subject to applicable deletion rights and limitations.",
      "We therefore do not describe SendGrid data as being retained indefinitely or for a fixed period where no such period has been established.",
      "13.4 Data That Is No Longer Required",
      "When personal data is no longer required for the purpose for which it was collected, we will take reasonable steps to securely delete, anonymise, or otherwise dispose of it, subject to legal, regulatory, security, or legitimate business requirements that require continued retention.",
    ],
  },
  {
    title: "14. Your Data Protection Rights",
    content: [
      "Subject to applicable legal conditions and limitations, you may have the right to:",
      "• Be informed about how your personal data is processed;\n• Request access to personal data we hold about you;\n• Request correction of inaccurate or incomplete personal data;\n• Request deletion of your personal data;\n• Request restriction of certain processing;\n• Object to certain processing;\n• Request portability of applicable personal data;\n• Withdraw consent where processing is based on consent;\n• Object to certain direct marketing activities; and\n• Raise concerns regarding applicable automated decision-making.",
      "You may also lodge a complaint with the Nigeria Data Protection Commission (NDPC) where you believe your personal data rights have not been adequately addressed.",
      "Some rights are subject to exceptions under applicable law. For example, we may need to retain certain information where required by law or where necessary to establish, exercise, or defend legal claims.",
    ],
  },
  {
    title: "15. Rights of Parents and Guardians",
    content: [
      "Where we process personal data relating to a child, a parent or legal guardian may, subject to applicable verification and legal requirements, request information about the child's personal data and exercise applicable rights on the child's behalf.",
      "Parents or guardians may contact us regarding:",
      "• Access to the child's personal data;\n• Correction of inaccurate information;\n• Deletion requests;\n• Withdrawal of parental consent where consent is the applicable basis; or\n• Other concerns regarding the child's personal data.",
      "We may take reasonable steps to verify the identity and authority of a person making a request concerning a child.",
    ],
  },
  {
    title: "16. Cookies and Analytics",
    content: [
      "Prep AI may use cookies, logs, and similar technologies necessary to operate, secure, analyse, and improve the Platform.",
      "We may use analytics tools to understand how the Platform is used and to identify technical or performance issues.",
      "Where applicable, users may have control over certain cookie or similar technology settings through their browser or device.",
      "Disabling certain technologies may affect some Platform functionality.",
    ],
  },
  {
    title: "17. Data Accuracy and Your Responsibility",
    content: [
      "We take reasonable steps to ensure that personal data we process is accurate and appropriate for the purposes for which it is used.",
      "You should provide accurate information when creating or using your account and notify us where information requiring correction is identified.",
      "You should also avoid submitting unnecessary personal or sensitive information through AI prompts, practice questions, or other free-text areas of Prep AI.",
    ],
  },
  {
    title: "18. Data Breaches and Incident Response",
    content: [
      "We maintain measures designed to prevent, detect, investigate, and respond to unauthorised access, loss, disclosure, or other incidents involving personal data.",
      "Where a personal data breach occurs, Publica AI will assess and respond to the incident in accordance with applicable legal and regulatory requirements, including any applicable notification obligations.",
    ],
  },
  {
    title: "19. Third-party Links and Services",
    content: [
      "Prep AI may contain links to or integrations with third-party services.",
      "This Privacy Policy applies to personal data processed by Publica AI through Prep AI. Third-party services may have their own privacy policies and terms.",
      "We encourage users to review the privacy information of third-party services before providing information directly to them.",
    ],
  },
  {
    title: "20. Changes to This Privacy Policy",
    content: [
      "We may update this Privacy Policy from time to time to reflect changes in:",
      "• Prep AI's features or data-processing practices;\n• Applicable laws or regulatory requirements;\n• Our technology and security measures; or\n• Our privacy practices.",
      "When we make material changes, we will provide an appropriate notice and update the effective date of this Privacy Policy.",
    ],
  },
  {
    title: "21. Contact Us",
    content: [
      "If you have questions, concerns, or requests regarding this Privacy Policy or the processing of your personal data, you may contact us:",
      "Publica AI Limited\nNo. 8 Montgomery Street,\nYaba, Lagos, Nigeria",
      "Email: privacy@prepai.education\nPhone: +234 911 243 0000",
      "You may also contact the Nigeria Data Protection Commission where you have concerns about the processing of your personal data or wish to lodge a complaint with the supervisory authority.",
    ],
  },
];

export function PrivacyPolicyScreen() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="app-column">
      <div className="flex-1 overflow-y-auto px-5 pb-10 pt-4">
        <BackButton to="/profile" />

        <h1 className="m-0 pt-5 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.25]">
          Privacy Policy
        </h1>
        <p className="m-0 pt-1 text-[13px] text-text-muted">
          Effective: {EFFECTIVE} · Last updated: {LAST_UPDATED}
        </p>

        <div className="mt-6 flex flex-col gap-3">
          {sections.map((section, i) => {
            const open = openIndex === i;
            return (
              <div
                key={section.title}
                className="overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface"
              >
                <button
                  type="button"
                  onClick={() => toggle(i)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                >
                  <span className="text-[14px] font-semibold leading-[1.35]">{section.title}</span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className={`shrink-0 text-text-muted transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {open ? (
                  <div className="border-t border-border px-4 pb-4 pt-3">
                    {section.content.map((paragraph, j) => (
                      <p
                        key={j}
                        className="m-0 whitespace-pre-line pt-2 text-[13px] leading-[1.6] text-text-muted first:pt-0"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <footer className="mt-8 text-center">
          <p className="m-0 text-[12px] text-text-muted">
            &copy; {new Date().getFullYear()} Publica AI Limited. RC. 7884242.
          </p>
          <p className="m-0 pt-1 text-[12px] text-text-muted">
            No. 8 Montgomery Street, Yaba, Lagos, Nigeria
          </p>
          <p className="m-0 pt-1 text-[12px] text-text-muted">
            privacy@prepai.education &middot; +234 911 243 0001
          </p>
        </footer>
      </div>
    </div>
  );
}
